import React, { useState } from "react";
import { FaCalculator } from "react-icons/fa";
import { FieldArray, Field, ErrorMessage } from "formik";

import { fetchData } from "../../../helpers/fetchData";
import TextInput from "../../../shared/TextInput";
import DropdownField from "../../../shared/DropdownField";
import { DeleteIcon } from "../../../utils/TableActionIcons";
import SwitchInput from "../../../shared/SwitchInput";
import {
  approvalStatusOptions,
  searchedPriceItemsOptionsFormat,
  visitDiscountOptions,
} from "../../../constants/VisitsConstants";
import SearchPriceListField from "../../../shared/SearchPriceListField";
import CostManagementModal from "../../../shared/CostManagementModal";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { getDepartmentDiscountLimit, isBusiness42Receptionist } from "../../../utils/businessLevelAccess";
import { CalculateAgeInYears } from "../../../utils/CalculateAge";
import {
  getItemStockCode,
  getOutOfStockMessage,
  isOutOfStockItem,
  isQuantityOverStock,
  showStockLimitToast,
} from "../../../utils/StockAlerts";

const VisitPriceItems = ({
  selectedPayerData,
  selectedPatientData,
  isAddCheckBox = false,
  isAlowActions = true,
  masterPriceCheck = false,
  masterPriceVat = false,
  department_id,
  showSearch = true,
  showTable = true,
  showNeedApproval = true,
  buildNewItem,
}) => {
  const { values, setFieldValue } = useFormikContext();
  const [itemLoading, setItemLoading] = useState(false);
  const [searchedItem, setSearchedItem] = useState("");
  const [itemsSuggestions, setItemsSuggestions] = useState([]);
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);

  const { t, i18n } = useTranslation();
  const isAdmin = useSelector(
    (state) => state.auth.user?.role?.name_en == "Admin",
  );
  const isManager = useSelector(
    (state) => state.auth.user?.role?.name_en == "manager",
  );
  const isReceptionist = useSelector(
    (state) => state.auth.user?.role?.name_en == "Receptionist",
  );
  const isAccountant = useSelector(
    (state) => state.auth.user?.role?.name_en == "Accountant",
  );
  const { user, discountSettings } = useSelector((state) => state.auth);
  const maxDiscount = getDepartmentDiscountLimit(user, department_id, discountSettings);
  const isBuisness42Receptionist = isBusiness42Receptionist(user);

  const getMaxFixedDiscount = (item) => {
    if (!isBuisness42Receptionist) return undefined;
    const sessions = Number(item?.number_of_sessions);
    const unitPrice = Number(item?.unit_price);
    const baseAmount =
      sessions > 1 ? unitPrice : Number(item?.original_price) || unitPrice;
    return Math.round(baseAmount * (maxDiscount / 100) * 100) / 100;
  };

  const handleCostSave = (totalManageCosts) => {
    if (selectedItemIndex === null) return;

    const numberOfSessions =
      values?.items[selectedItemIndex]?.number_of_sessions || 1;
    const unitPrice =
      numberOfSessions > 1
        ? (totalManageCosts / numberOfSessions).toFixed(2)
        : totalManageCosts;

    setFieldValue(
      `items[${selectedItemIndex}].original_price`,
      totalManageCosts,
    );
    setFieldValue(`items[${selectedItemIndex}].unit_price`, unitPrice);

    if (values?.items[selectedItemIndex]?.is_approval) {
      setFieldValue(`items[${selectedItemIndex}].approved_amount`, unitPrice);
    }
  };

  const handleSearchItems = async (event) => {
    setItemLoading(true);
    try {
      if (!values?.payerName) {
        toast.error(
          "Payer selection is required before proceeding with items.",
        );
        return;
      }

      let payload = {
        search: event?.query,
        payer_id: values?.payerName || "",
        item_type: selectedPatientData?.patientType || "insurance",
        is_master_price: masterPriceCheck || false,
        department_id: department_id,
        age: CalculateAgeInYears(selectedPatientData?.dob),
        gender: selectedPatientData?.gender || "other",
      };

      const response = await fetchData(
        "POST",
        "/v3/pricelists/search",
        payload,
      );
      const dataWithMasterPriceFlag = response?.data?.map((item) => ({
        ...item,
        _is_master_price: masterPriceCheck || false,
        _is_vat_applicateable: masterPriceVat === true,
      }));

      setItemsSuggestions(
        dataWithMasterPriceFlag
          ? searchedPriceItemsOptionsFormat(
            dataWithMasterPriceFlag,
            values?.payerName,
          )
          : [],
      );
    } catch (e) {
      console.log(e);
    } finally {
      setItemLoading(false);
    }
  };

  const handleSelectSearchedItem = (event) => {
    if (!event?.value?.value) return false;

    let itemData = event?.value?.item;
    let existingItems = [...(values?.items || [])];
    let existingIndex = existingItems.findIndex(
      (item) => item.item_id === itemData?.id,
    );

    //let selectedItemPayer = itemData?.payer_details?.find((data)=>(data?.payer_id==selectedPayerData?.his_payer_id));
    let selectedItemPayer = itemData?.payer_details?.[0];
    let isMasterPrice =
      masterPriceCheck || selectedItemPayer?.payer?.is_master_price;
    const payerVat = isMasterPrice
      ? itemData?._is_vat_applicateable === true
      : selectedItemPayer?.payer?.is_vat_applicable === true;
    const selectedServiceDiscount =
      selectedItemPayer?.payer?.service_discount?.find(
        (item) => item?.service_id === itemData?.price_list_type?.id,
      );
    const itemCode = getItemStockCode(itemData, selectedItemPayer);

    if (isOutOfStockItem(itemData)) {
      toast.error(getOutOfStockMessage(itemData, selectedItemPayer));
      setSearchedItem("");
      return null;
    }

    if (existingIndex !== -1) {
      const nextQuantity = Number(existingItems[existingIndex].quantity) + 1;
      if (isQuantityOverStock(itemData, nextQuantity)) {
        showStockLimitToast(toast, itemData?.stock);
        setSearchedItem("");
        return null;
      }
      existingItems[existingIndex].quantity = nextQuantity;

      if (selectedItemPayer?.is_approval) {
        existingItems[existingIndex].approved_qty =
          Number(existingItems[existingIndex].approved_qty) + 1;
      }

      existingItems[existingIndex].total_quantity += 1;
    } else {
      const number_of_sessions = itemData?.session
        ? itemData?.number_of_sessions
        : 0;
      const basePrice = isMasterPrice
        ? itemData?.master_price
        : (selectedItemPayer?.payer_price ?? 0);
      let newItem = buildNewItem
        ? buildNewItem({
          itemData,
          selectedItemPayer,
          selectedServiceDiscount,
          isMasterPrice,
          department_id,
          selectedPayerData,
          selectedPatientData,
          values,
        })
        : {
          item_name: selectedItemPayer?.non_standard_description,
          item_id: itemData?.id,
          is_stock: itemData?.is_stock,
          stock: itemData?.stock,
          number_of_sessions: number_of_sessions,
          daily_package_deal: itemData?.daily_package_deal,
          internal_pharmacy: itemData?.internal_pharmacy,
          is_consumable: itemData?.is_consumable || false,
          item_type: itemData?.price_list_type?.name_en,
          standard_code: itemData?.standard_code,
          standard_description: itemData?.standard_description,
          master_code: itemData?.non_standard_code,
          supergit_master_code:
            itemData?.source === 'sfda' ||
              itemData?.source === 'provider_pharmacy'
              ? itemData?.supergit_master_code || itemData?.non_standard_code
              : itemData?.supergit_master_code || null,
          manage_costs: itemData?.manage_costs || [],
          non_standard_code:
            itemCode ||
            selectedItemPayer?.agreed_code ||
            selectedItemPayer?.non_standard_code ||
            "",
          non_standard_description:
            itemData?.non_standard_description ||
            selectedItemPayer?.agreed_description_en ||
            selectedItemPayer?.non_standard_description ||
            "",
          non_standard_description_ar:
            itemData?.non_standard_description_ar ||
            selectedItemPayer?.agreed_description_ar ||
            selectedItemPayer?.non_standard_description_ar ||
            "",
          is_package: "false",
          payer_vat: payerVat,
          quantity: 1,
          total_quantity: 1,
          unit_price: (number_of_sessions
            ? basePrice / number_of_sessions
            : basePrice || 0
          ).toFixed(2),
          original_price: basePrice || 0,
          tax_amount: 0,
          factor: 0,
          net: 0,
          item_category:
            selectedItemPayer?.payer?.category || values?.patientType || "",
          is_approval: selectedItemPayer?.is_approval || false,
          ...(selectedItemPayer?.is_approval
            ? selectedItemPayer?.payer?.category === "insurance"
              ? {
                approved_qty: 1,
                approved_amount: Number(
                  (number_of_sessions
                    ? (isMasterPrice
                      ? itemData?.master_price
                      : selectedItemPayer?.payer_price ||
                      itemData?.master_price) / number_of_sessions
                    : isMasterPrice
                      ? itemData?.master_price || 0
                      : selectedItemPayer?.payer_price ||
                      itemData?.master_price ||
                      0
                  ).toFixed(2),
                ),
                approval_status: "pending",
                approval_reason: "Pending due to insurance item requiring approval",
              }
              : {
                approved_qty: 1,
                approved_amount: Number(
                  (number_of_sessions
                    ? (isMasterPrice
                      ? itemData?.master_price
                      : selectedItemPayer?.payer_price ||
                      itemData?.master_price) / number_of_sessions
                    : isMasterPrice
                      ? itemData?.master_price || 0
                      : selectedItemPayer?.payer_price ||
                      itemData?.master_price ||
                      0
                  ).toFixed(2),
                ),
                approval_status: "approved",
              }
            : {}),
          discount_amount:
            selectedServiceDiscount?.patient_discount ||
            selectedItemPayer?.payer?.generals?.discount ||
            selectedItemPayer?.discount_percentage ||
            selectedItemPayer?.discount_amount ||
            itemData?.discount_percentage ||
            0,
          discount_type: "percentage",
          patient_share:
            selectedItemPayer?.payer?.category == "cash" ||
              values?.patientType == "cash"
              ? 100
              : selectedServiceDiscount?.patient_share ||
              selectedPayerData?.patient_share,
          patientShareAmount: 0,
          selected_category: itemData?.price_list_type?.category || "",
          payer_share: "",
          payer_id: selectedItemPayer?.payer_id ?? "",
          days_supply: 0,
          checked: true,
          invoice_no: 0,
          company_invoice_no: 0,
          tax_check:
            selectedPatientData?.nationality?.toLowerCase() === "saudi" ||
              itemData?.apply_vat == false
              ? false
              : true,
          order_changeable_price: itemData?.order_changeable_price,
        };
      console.log(newItem);

      existingItems.push(newItem);
    }
    setFieldValue("items", existingItems);
    setSearchedItem("");
  };
  const hasInsuranceItem = values?.items?.some(
    (it) => it?.item_category === "insurance",
  );
  const hasApprovalItem = values?.items?.some((it) => it?.is_approval === true);
  const requiresApproval = hasApprovalItem;
  const showSessionPrice = values?.items?.some(
    (item) => Number(item?.number_of_sessions) > 1,
  );

  return (
    <div className="col-span-12">
      <div className="w-full">
        {isAlowActions && showSearch && (
          <SearchPriceListField
            disabled={!values?.payerName}
            searchedItem={searchedItem}
            loading={itemLoading}
            payerID={values?.payerName}
            itemsSuggestions={itemsSuggestions}
            setSearchedItem={setSearchedItem}
            handleSelectSearchedItem={handleSelectSearchedItem}
            handleSearchItems={handleSearchItems}
            showNeedApproval={showNeedApproval}
          />
        )}

        {showTable && (
          <div>
            <div
              className="w-full border border-gray-200 mt-2 overflow-auto
            "
            >
              <table className="w-full">
                <thead className="rounded !px-4 sticky top-0 z-10 bg-gray-50">
                  <tr>
                    {isAddCheckBox && (
                      <th className={`table-header !font-bold`}></th>
                    )}
                    <th className={`table-header !font-bold ps-3`}>
                      {" "}
                      {t("Code")}
                    </th>
                    <th className={`table-header !font-bold`}> {t("Name")}</th>
                    <th className={`table-header !font-bold `}> {t("Type")}</th>
                    <th className={`table-header !font-bold `}>
                      {" "}
                      {t("Sessions")}
                    </th>
                    <th className={`table-header !font-bold`}>
                      {" "}
                      {t("U.Price")}
                    </th>
                    {showSessionPrice && (
                      <th className={`table-header !font-bold`}>
                        {" "}
                        {t("S.Price")}
                      </th>
                    )}
                    <th className={`table-header !font-bold`}> {t("Qty")}</th>

                    <th className={`table-header !font-bold`}>
                      {t("P.Share")}
                    </th>
                    <th className={`table-header !font-bold`}>{t("Disc")}</th>
                    {(isAccountant || isAdmin || isManager) && (
                      <th className={`table-header !font-bold`}>{t("Vat")}</th>
                    )}
                    <th className={`table-header !font-bold `}>
                      {" "}
                      {t("Apprv.")}
                    </th>
                    {requiresApproval ? (
                      <>
                        <th className={`table-header !font-bold `}>
                          {" "}
                          {t("Apprv Qty")}
                        </th>
                        <th className={`table-header !font-bold `}>
                          {" "}
                          {t("Apprv Amnt")}
                        </th>
                        <th className={`table-header !font-bold`}>
                          {" "}
                          {t("Apprv Status")}
                        </th>
                      </>
                    ) : null}
                    {isAlowActions && (
                      <th className={`table-header !font-bold !text-center`}>
                        {t("Actions")}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {values?.items?.length > 0 ? (
                    values?.items?.map((item, index) => {
                      const isOutOfStockRow = Boolean(item?.out_of_stock);
                      const isLowStockRow = Boolean(item?.low_stock);
                      const isDisabledStockRow =
                        isOutOfStockRow || isLowStockRow;
                      const stockLabel = isOutOfStockRow
                        ? t("Out of stock")
                        : t("Low stock");
                      return (
                        <tr
                          key={index}
                          className={`border-b ${isDisabledStockRow
                            ? "bg-red-50 opacity-70"
                            : "bg-white"
                            }`}
                          title={isDisabledStockRow ? stockLabel : ""}
                        >
                          {isAddCheckBox && (
                            <td className="text-16-gray !font-semibold ps-2">
                              <input
                                disabled={isDisabledStockRow}
                                type="checkbox"
                                name={`items[${index}].checked`}
                                className="bg-gray-100 border border-gray-200 h-[18px] rounded-lg w-[18px] mt-2"
                                checked={item?.checked}
                                onChange={(e) =>
                                  setFieldValue(
                                    `items[${index}].checked`,
                                    e?.target?.checked,
                                  )
                                }
                              />
                            </td>
                          )}
                          <td className="text-14-gray !font-semibold ps-3">
                            {item?.non_standard_code || item?.standard_code}
                          </td>
                          <td className="text-14-gray !font-semibold">
                            <div className="flex items-center gap-2">
                              <span>
                                {i18n.language == "en"
                                  ? item?.non_standard_description
                                  : item?.non_standard_description_ar ||
                                  item?.item_name}
                              </span>
                              {isDisabledStockRow && (
                                <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700">
                                  {stockLabel}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="text-14-gray  !font-semibold">
                            {item?.item_category || "-"}
                          </td>
                          <td className="text-14-gray  !font-semibold text-center">
                            {item?.number_of_sessions || "1"}
                          </td>
                          <td>
                            <TextInput
                              readOnly={
                                isDisabledStockRow ||
                                (isAdmin || isAccountant || isManager
                                  ? false
                                  : !item?.order_changeable_price)
                              }
                              type="number"
                              step="any"
                              name={`items[${index}].original_price`}
                              className="!w-20 !text-lg "
                              hideErrorText={true}
                              onChange={(e) => {
                                const value =
                                  e.target.value === ""
                                    ? ""
                                    : Number(e.target.value);
                                setFieldValue(
                                  `items[${index}].original_price`,
                                  value,
                                );
                                const numberOfSessions =
                                  item?.number_of_sessions || 1;
                                const unitPrice =
                                  numberOfSessions > 1
                                    ? (value / numberOfSessions).toFixed(2)
                                    : value;
                                setFieldValue(
                                  `items[${index}].unit_price`,
                                  unitPrice,
                                );
                                if (item?.is_approval == true) {
                                  setFieldValue(
                                    `items[${index}].approved_amount`,
                                    unitPrice,
                                  );
                                }
                              }}
                              min={0}
                            />
                          </td>
                          {showSessionPrice && (
                            <td>
                              {Number(item?.number_of_sessions) > 1 && (
                                <TextInput
                                  readOnly={
                                    isDisabledStockRow ||
                                    (!isAdmin && !isAccountant && !isManager)
                                  }
                                  type="number"
                                  step="any"
                                  name={`items[${index}].unit_price`}
                                  className="!w-20 !text-lg "
                                  hideErrorText={true}
                                  onChange={(e) => {
                                    const value =
                                      e.target.value === ""
                                        ? ""
                                        : Number(e.target.value);
                                    setFieldValue(
                                      `items[${index}].unit_price`,
                                      value,
                                    );
                                    if (item?.is_approval == true) {
                                      setFieldValue(
                                        `items[${index}].approved_amount`,
                                        value,
                                      );
                                    }
                                  }}
                                  min={0}
                                />
                              )}
                            </td>
                          )}

                          <td>
                            <TextInput
                              disabled={!isAlowActions || isDisabledStockRow}
                              name={`items[${index}].quantity`}
                              hideErrorText={true}
                              onChange={(e) => {
                                const value =
                                  e.target.value === ""
                                    ? ""
                                    : Number(e.target.value);
                                if (isQuantityOverStock(item, value)) {
                                  showStockLimitToast(toast, item?.stock);
                                  return;
                                }
                                setFieldValue(
                                  `items[${index}].quantity`,
                                  value,
                                );
                                if (item?.is_approval == true) {
                                  setFieldValue(
                                    `items[${index}].approved_qty`,
                                    value,
                                  );
                                }
                              }}
                              className="!w-20 !text-lg "
                              type="number"
                              step="any"
                              min={0}
                            />
                          </td>

                          <td>
                            <TextInput
                              disabled={!isAlowActions || isDisabledStockRow}
                              name={`items[${index}].patient_share`}
                              className="!w-20 !text-lg "
                              hideErrorText={true}
                              type="number"
                              step="any"
                              min={0}
                            />
                          </td>
                          <td>
                            <div className="flex gap-x-2 items-center">
                              <DropdownField
                                disabled={!isAlowActions || isDisabledStockRow}
                                name={`items[${index}].discount_type`}
                                value={item?.discount_type || "percentage"}
                                options={visitDiscountOptions(t)}
                                optionLabel="label"
                                placeholder={t("Selectdiscounttype")}
                                parentClass="!w-fit"
                                hideErrorText={true}
                                onChange={(e) => {
                                  const newType = e.value;
                                  setFieldValue(
                                    `items[${index}].discount_type`,
                                    newType,
                                  );
                                  const currentAmount =
                                    Number(item?.discount_amount) || 0;
                                  const maxValue =
                                    newType === "percentage"
                                      ? maxDiscount
                                      : getMaxFixedDiscount(item) ??
                                        Number.MAX_SAFE_INTEGER;
                                  setFieldValue(
                                    `items[${index}].discount_amount`,
                                    Math.min(currentAmount, maxValue),
                                  );
                                }}
                              />
                              <TextInput
                                disabled={
                                  !isAlowActions ||
                                  isDisabledStockRow ||
                                  (!isAdmin && !isReceptionist && !isManager)
                                }
                                type="number"
                                step="any"
                                name={`items[${index}].discount_amount`}
                                className="!w-20 !text-lg "
                                hideErrorText={true}
                                min={0}
                                max={
                                  item?.discount_type === "percentage"
                                    ? maxDiscount
                                    : getMaxFixedDiscount(item)
                                }
                                onChange={(event) => {
                                  const enteredValue = Number(event.target.value) || 0;
                                  const maxValue =
                                    item?.discount_type === "percentage"
                                      ? maxDiscount
                                      : getMaxFixedDiscount(item) ?? Number.MAX_SAFE_INTEGER;
                                  const value = Math.min(enteredValue, maxValue);
                                  setFieldValue(`items[${index}].discount_amount`, value);
                                }}
                              />
                            </div>
                          </td>
                          {(isAccountant || isAdmin || isManager) && (
                            <td>
                              <SwitchInput
                                disabled={isDisabledStockRow}
                                parentClass="mt-3"
                                value={values?.items[index]?.tax_check}
                                onChange={(event) => {
                                  if (isDisabledStockRow) return;
                                  setFieldValue(
                                    `items[${index}].tax_check`,
                                    event.target.value,
                                  );
                                }}
                              />
                            </td>
                          )}
                          <td>
                            <SwitchInput
                              disabled
                              parentClass="mt-3"
                              value={values?.items[index]?.is_approval}
                              onChange={(event) => {
                                setFieldValue(
                                  `items[${index}].is_approval`,
                                  event.target.value,
                                );
                              }}
                            />
                          </td>
                          {item?.is_approval ? (
                            <>
                              <td>
                                <TextInput
                                  readOnly
                                  disabled={isDisabledStockRow}
                                  type="number"
                                  step="any"
                                  name={`items[${index}].approved_qty`}
                                  className="!w-20 !text-lg "
                                  hideErrorText={true}
                                  min={0}
                                />
                              </td>
                              <td>
                                <TextInput
                                  readOnly
                                  disabled={isDisabledStockRow}
                                  type="number"
                                  step="any"
                                  name={`items[${index}].approved_amount`}
                                  className="!w-20 !text-lg "
                                  hideErrorText={true}
                                  min={0}
                                />
                              </td>
                              <td>
                                <DropdownField
                                  disabled
                                  value={item?.approval_status}
                                  options={approvalStatusOptions(t)}
                                  placeholder={t("Select Status")}
                                  name={`items[${index}].approval_status`}
                                  className="!w-fit"
                                  hideErrorText={true}
                                />
                              </td>
                            </>
                          ) : (
                            values?.items?.find((item) => item.is_approval) && (
                              <>
                                <td></td>
                                <td></td>
                                <td></td>
                              </>
                            )
                          )}
                          {isAlowActions && (
                            <td>
                              <p className="flex justify-end items-center">
                                {item?.manage_costs?.length > 0 && (
                                  <FaCalculator
                                    size={15}
                                    className={`${isDisabledStockRow
                                      ? "text-gray-300 cursor-not-allowed"
                                      : "text-blue cursor-pointer"
                                      } mx-1`}
                                    onClick={() => {
                                      if (isDisabledStockRow) return;
                                      setSelectedItemIndex(index);
                                      setCostModalOpen(true);
                                    }}
                                    title={t("Cost Management")}
                                  />
                                )}
                                <DeleteIcon
                                  iconSize="20"
                                  onClick={() => {
                                    const filteredItems = values?.items?.filter(
                                      (item, i) => i !== index,
                                    );
                                    setFieldValue("items", filteredItems);
                                  }}
                                />
                              </p>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={
                          requiresApproval
                            ? showSessionPrice
                              ? 12
                              : 11
                            : showSessionPrice
                              ? 9
                              : 8
                        }
                        className="py-2 text-center text-gray-400 italic font-medium"
                      >
                        {t("No items added yet")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <CostManagementModal
        isVisible={costModalOpen}
        onClose={() => setCostModalOpen(false)}
        selectedItemIndex={selectedItemIndex}
        setFieldValue={setFieldValue}
        formValues={values}
        onSave={handleCostSave}
      />
    </div>
  );
};

export default React.memo(VisitPriceItems);
